import { useContext } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import folderMoveIcon from '@atom/components/common/svgIcons/folderMove.svg';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { Icon, IconButton, List, Menu } from '@atom/mui';
import {
  ArrowDropDownIcon,
  ArrowRightIcon,
  CreateNewFolderIcon,
  DeleteIcon,
  FolderIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { MediaFolderTree } from '@atom/types/media';

import { ROOT_FOLDER_ID } from '../constants';
import FilesTabContext from '../FilesTabContext';
import { canCreateSubfolder } from '../filesTabUtilities';
import { FolderModalType } from '../types';

import FolderTreeContext from './FolderTreeContext';

import classes from './folderTree.module.css';

const { ListItemButton } = List;
const { MenuItem } = Menu;

type FolderTreeItemProps = {
  tree: MediaFolderTree;
  level: number;
  ancestors: string[];
};

const FolderTreeItem = ({ tree, level, ancestors }: FolderTreeItemProps) => {
  const { id, name, children } = tree;

  const { expandedIds, onToggleExpand, openModal } =
    useContext(FolderTreeContext);
  const {
    folderTree,
    selectedFolderId,
    selectFolder,
    canCreate,
    canUpdate,
    canDelete,
  } = useContext(FilesTabContext);

  const isRoot = id === folderTree?.id;
  const isExpanded = expandedIds.has(id);
  const hasChildren = !R.isNil(children);
  const isSelected = selectedFolderId === id;

  const showFolderCreate =
    canCreate && canCreateSubfolder(ancestors.length - 1);
  const showActions = isRoot
    ? showFolderCreate
    : canUpdate || canDelete || showFolderCreate;

  const leftIcon = (
    <div className={classes.treeRowToggle}>
      {hasChildren ? (
        <IconButton
          onClick={event => {
            event.stopPropagation();
            onToggleExpand(id);
          }}
          size="small"
        >
          <Icon
            color={colors.neutral.dim}
            icon={isExpanded ? ArrowDropDownIcon : ArrowRightIcon}
          />
        </IconButton>
      ) : (
        <span className={classes.treeRowTogglePlaceholder} />
      )}
      <Icon icon={FolderIcon} />
    </div>
  );

  const rightActions = showActions ? (
    <Menu
      IconButtonProps={{ size: 'small' }}
      dataCyLabel={`filesTabTreeFolderMenu-${name}`}
    >
      {!isRoot && canUpdate && (
        <MenuItem
          startAdornment={
            <Icon>
              <img src={renameIcon} />
            </Icon>
          }
          onClick={() => openModal({ type: FolderModalType.EDIT, id, name })}
          data-cy={`filesTabTreeFolderRename-${name}`}
        >
          Rename
        </MenuItem>
      )}
      {showFolderCreate && (
        <MenuItem
          startAdornment={<Icon icon={CreateNewFolderIcon} />}
          onClick={() => openModal({ type: FolderModalType.CREATE_SUB, id })}
          data-cy={`filesTabTreeFolderCreateSub-${name}`}
        >
          Create Folder
        </MenuItem>
      )}
      {!isRoot && canUpdate && (
        <MenuItem
          startAdornment={
            <Icon>
              <img src={folderMoveIcon} />
            </Icon>
          }
          onClick={() =>
            openModal({ type: FolderModalType.MOVE, id, ancestors })
          }
          data-cy={`filesTabTreeFolderMove-${name}`}
        >
          Move to Folder
        </MenuItem>
      )}
      {!isRoot && canDelete && (
        <MenuItem
          startAdornment={<Icon icon={DeleteIcon} />}
          onClick={() => openModal({ type: FolderModalType.DELETE, id, name })}
          data-cy={`filesTabTreeFolderDelete-${name}`}
        >
          Delete
        </MenuItem>
      )}
    </Menu>
  ) : (
    <div className={classes.treeRowEmptyAction} />
  );

  return (
    <ListItemButton
      disableGutters
      className={clsx(classes.treeRow, isSelected && classes.treeRowSelected)}
      style={{ paddingLeft: `${level}rem` }}
      onClick={() => selectFolder(isRoot ? ROOT_FOLDER_ID : id)}
    >
      {leftIcon}
      <div className={classes.treeRowName}>
        <TextOverflowTooltip text={name} fontSize={fonts.md} />
      </div>
      {rightActions}
    </ListItemButton>
  );
};

export default FolderTreeItem;
